/**
 * Narrowing every page to a chosen set of channels.
 *
 * WHY THIS IS NOT A ONE-LINE `.filter()` IN THE PAGE. The dashboard's totals come
 * from two places that look interchangeable and are not: spend, leads,
 * engagements and video views are summed off `data.channels`, while impressions,
 * clicks, conversions and conversionRate arrive pre-aggregated in `data.metrics`.
 * `kpiCpa` already mixes them — `costPerConversion(kpiSpend, data.metrics.conversions)`.
 * Filter only the channels and that line becomes filtered spend over UNFILTERED
 * conversions, printed as cost-per-conversion with no sign that anything is wrong.
 * So the filter has to produce BOTH, from one set of rows.
 *
 * That it can is not luck. `fetchCampaignMetrics` and `fetchChannelPerformance`
 * read the SAME `get_campaign_summary` rows over the same range and both skip
 * `analytics`; one reduces them, the other groups them by platform. So
 * `metricsFromChannels` is not a re-derivation that might disagree — it is the
 * same arithmetic over a subset, and over the full set it returns what the
 * service already returned.
 *
 * MEASURED before this was written, on XPeng's live 2026-07-01..10-07 rollup:
 * reducing the raw rows and grouping-then-summing them agree on impressions,
 * clicks, conversions and spend exactly (฿2,941,842.15, equality tested in SQL,
 * not eyeballed). So switching the page's totals to this function changes no
 * figure while the filter is off.
 *
 * @module services/channelFilter
 */

import { measuredRoas } from "@/components/mmm/unmeasured";
import type { CampaignMetricsData, ChannelPerformance } from "./mediaIntelligenceTypes";

/**
 * The channels a reader may tick.
 *
 * Google Analytics is deliberately absent, and not merely as a preference: on the
 * live XPeng rollup its rows carry 2,627,966 "clicks" and 1,443,952
 * "conversions" — site-wide sessions and events — against 1,349,936 and 5,041 for
 * every ad channel combined. Both producers already drop it
 * (`fetchCampaignMetrics` filters `platform !== 'analytics'`,
 * `fetchChannelPerformance` returns early on it) and it has its own Web Analytics
 * card. A checkbox sitting beside Facebook would invite someone to add a 28,600%
 * inflation to an ad conversion total.
 *
 * Order is display order: by how much spend these channels carry in practice.
 */
export const AD_CHANNEL_IDS = ["facebook", "google", "tiktok", "line"] as const;

export type AdChannelId = (typeof AD_CHANNEL_IDS)[number];

/** What the checkboxes hold. A plain Set so callers can test membership cheaply. */
export type ChannelSelection = ReadonlySet<string>;

/**
 * Whether a channel's rows belong on screen.
 *
 * AN EMPTY SELECTION MEANS EVERYTHING, not nothing. Two ways to reach one: a
 * reader unticks the last box, or localStorage hands back a stale empty set from
 * before this shipped. Both must leave the page intact — a dashboard that blanks
 * every figure because of a remembered filter looks broken, and the reader has no
 * reason to suspect a checkbox they cannot see.
 *
 * A channel this filter does not govern — `analytics` today — always passes. The
 * filter owns the ad channels; the Web Analytics card is not its to hide, and
 * silently dropping it would make the page disagree with itself about whether GA
 * exists.
 */
export const isChannelShown = (selected: ChannelSelection, channelId: string): boolean => {
  if (selected.size === 0) return true;
  if (!(AD_CHANNEL_IDS as readonly string[]).includes(channelId)) return true;
  return selected.has(channelId);
};

/**
 * Whether the reader is actually looking at a subset.
 *
 * All four ticked is the same view as none ticked, so the UI must not announce a
 * filter in that case — a badge saying "filtered" over unfiltered numbers is a
 * claim about the data that is not true.
 */
export const isFiltering = (selected: ChannelSelection): boolean =>
  selected.size > 0 && selected.size < AD_CHANNEL_IDS.length;

/** The rows that survive the selection, in their original order. */
export const filterChannels = <T extends { id: string }>(
  channels: readonly T[],
  selected: ChannelSelection,
): T[] => channels.filter((c) => isChannelShown(selected, c.id));

const sum = <T,>(rows: readonly T[], pick: (row: T) => number | null | undefined): number =>
  rows.reduce((total, row) => total + (Number(pick(row)) || 0), 0);

/**
 * The page's blended totals, rebuilt from a set of channel rows.
 *
 * Every line below mirrors `fetchCampaignMetrics` deliberately, including the
 * parts that look like bugs and are not:
 *
 *   - `ctr` is guarded on IMPRESSIONS and `conversionRate` on CLICKS. Each guard
 *     protects its own denominator. Swapping them is how "Infinity%" reached the
 *     screen once: a view-through conversion is conversions with no clicks, and
 *     Facebook and Google both report those.
 *   - `leads` is the conversion count again. There is no signup metric in this
 *     database; the field survives because ~90 call sites read it, and nothing on
 *     screen presents it as a lead count any more.
 *   - `roas` is null, not 0, when no revenue was reported. Zero revenue means
 *     "this channel never sent conversion value" far more often than it means it
 *     earned nothing, and the two must not render alike.
 *   - every `*Growth` is null. Growth needs a second window and this is given
 *     one. In this codebase 0 is a MEASUREMENT — the one that says a figure did
 *     not move — so returning 0 here would put a fabricated "no change" in the
 *     hero chip and in the AI's prompt.
 */
export const metricsFromChannels = (channels: readonly ChannelPerformance[]): CampaignMetricsData => {
  const impressions = sum(channels, (c) => c.impressions);
  const clicks = sum(channels, (c) => c.clicks);
  const conversions = sum(channels, (c) => c.conversions);
  const cost = sum(channels, (c) => c.cost);
  const revenue = sum(channels, (c) => c.revenue);

  return {
    impressions,
    impressionsGrowth: null,
    ctr: impressions > 0 ? (clicks / impressions) * 100 : 0,
    ctrGrowth: null,
    clicks,
    cpl: conversions > 0 ? cost / conversions : 0,
    leads: conversions,
    leadsGrowth: null,
    conversions,
    conversionsGrowth: null,
    cplChange: null,
    conversionRate: clicks > 0 ? (conversions / clicks) * 100 : 0,
    monthlyRevenue: revenue,
    monthlySpend: cost,
    roas: measuredRoas(revenue, cost),
  };
};
